import React from 'react';

/**
 * Utilities
 */
import { Consumption, Employee } from '../../models/WorkReportSubmodels';
import { fetchStaffHoursCategories } from "../../utils/api";

/**
 * MUI Components
 */
import {
    Box,
    Button,
    Typography,
    Card,
    CardHeader,
    CardContent,
    IconButton,
    Menu,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    ListSubheader,
    TextField,
    Divider
} from '@mui/material';
import Fab from '@mui/material/Fab';
import PlusIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import Switch from '@mui/material/Switch';


/**
 * Components
 */
import VehicleUsageFields from './Fields/VehicleUsageFields';
import AddEmployeeHoursForm from './AddEmployeeHoursForm';
import AddConsumptionForm from './AddConsumptionForm';
import AddVehicleForm from './AddVehicleForm';
import AddExtrasForm from './AddExtrasForm';
import PhotoManager from './PhotoManager';

/**
 * Context
 */
import { ReportFormContext } from '../../contexts/ReportFormContext';

/**
 * Hooks
 */
import { useState, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSpecies, useActions, useStatuses } from '../../hooks/Works/Extras/Forestal/useForestal';
import useToken from "../../hooks/useToken";
import useCachedData from "../../hooks/useCachedData";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const styles = {
    dialogBox: {
        padding: 2,
    },
    flexList: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        paddingBottom: "70px",
    },
    topFabBox: { display: 'flex', justifyContent: 'end', p: 2 },
    cardHeader: { paddingBottom: 0 },
    cardContent: { paddingTop: 0 },
    cardMuteText: {
        color: 'rgba(0, 0, 0, 0.6)',
        fontSize: '0.85rem',
    },
    cardInfoText: {
        color: 'rgb(65, 133, 236)',
        fontSize: '0.85rem',
    }
};

/**
 * Tries to find the action area with the given ID from the planning.
 * @param {object} planning the whole planning object
 * @param {number} id the action area ID
 * @returns null|object
 */
function findActionAreaById(planning, id) {
    // Check planning and actionAreas exists
    if (!planning || !planning.work || !planning.work.work_action_areas) {
        return null;
    }

    return planning.work.work_action_areas.find((area) => area.id === id);
}

/**
 * Tries to find the action subarea with the given ID from the planning.
 * @param {object} planning the whole planning object
 * @param {number} id the action subarea ID
 * @returns null|object
 */
function findActionSubareaById(planning, id) {
    // Check planning and actionSubareas exists
    if (!planning || !planning.work || !planning.work.work_action_subareas) {
        return null;
    }

    return planning.work.work_action_subareas.find((subarea) => subarea.id === id);
}

/**
 * Tries to find the service with the given ID from the planning.
 * @param {object} planning the whole planning object
 * @param {number} id the action area ID
 * @returns null|object
 */
function findServiceById(planning, id) {
    // Check planning and services exists
    if (!planning || !planning.work || !planning.work.catalog_services) {
        return null;
    }
    return planning.work.catalog_services.find((service) => service.id === id);
}

/**
 * Tries to find the employee with the given ID from the planning.
 * @param {object} planning the whole planning object
 * @param {number} id the action area ID
 * @returns null|object
 */
function findEmployeeById(planning, id) {
    // Check planning and employees exists
    if (!planning || !planning.staff) {
        return null;
    }
    return planning.staff.find((employee) => employee.id === id);
}

const FormDialog = ({ open, handleClose, title, children }) => {
    return (
        <Dialog
            fullScreen
            open={open}
            onClose={handleClose}
            scroll="paper"
            TransitionComponent={Transition}
            aria-labelledby="dialog-title"
            aria-describedby="dialog-description">
            <DialogTitle id="dialog-title">
                <Typography variant="h6">{title}</Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {children}
            </DialogContent>
        </Dialog>
    );
};

const DialogConfirmation = ({ title, open, onResult, children }) => {
    const { t } = useTranslation();

    return (
        <React.Fragment>
            <Dialog
                open={open}
                TransitionComponent={Transition}
                keepMounted
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description">
                        {children}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={e => { e.preventDefault(); onResult(false); }}>{t('Cancel')}</Button>
                    <Button onClick={e => { e.preventDefault(); onResult(true); }}>{t('Ok')}</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

const CardListOfConsumables = ({ items, planning, onAction }) => {

    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [openDelete, setOpenDelete] = useState(false);
    const { report, setReport } = useContext(ReportFormContext);

    const fromIndex = () => {
        return items[selectedIndex] || null;
    };

    const handleMenuOpen = (event, index) => {
        setAnchorEl(event.currentTarget);
        setSelectedIndex(index);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleView = () => {
        // Handle view action
        let item = fromIndex();
        console.log('View', item, selectedIndex);
        handleMenuClose();
        setSelectedIndex(null);

        if (item) {
            if (typeof onAction === 'function') {
                onAction('view', item, selectedIndex);
            } else {
                console.warn('onAction is not a function!');
            }
        } else {
            console.error('Report object is null!', item);
        }
    };

    const handleEdit = () => {
        // Handle edit action
        let item = fromIndex();
        // console.log('Edit', item, selectedIndex);
        handleMenuClose();
        setSelectedIndex(null);

        if (item) {
            if (typeof onAction === 'function') {
                onAction('edit', item, selectedIndex);
            } else {
                console.warn('onAction is not a function!');
            }
        } else {
            console.error('Report object is null!', item);
        }
    };

    const handleDeleteConfirmation = (result) => {
        // Handle delete action
        if (result) {
            let item = fromIndex();
            if (report && item) {
                if (selectedIndex >= 0 && selectedIndex < report.consumptions.length) {
                    report.consumptions.splice(selectedIndex, 1);
                }
                setReport({ ...report });
            } else {
                console.error('On delete consumable: Report object missing or invalid index!', report, selectedIndex);
            }
        }
        setOpenDelete(false);
        handleMenuClose();
        setSelectedIndex(null);
    };

    const renderCardContent = (item) => {
        let actionArea = findActionAreaById(planning, item.workActionAreaId);
        let actionSubarea = findActionSubareaById(planning, item.workActionSubareaId);
        let service = findServiceById(planning, item.workServiceId);
        let employee = findEmployeeById(planning, item.sid);

        // Fallbacks. 
        // Try to get them from the item object. This will exists if the report data
        // is an edit and comes from an API call.
        if (typeof actionArea === 'undefined' || actionArea === null) {
            actionArea = (item?.work_action_areas &&
                Array.isArray(item.work_action_areas) &&
                item.work_action_areas.length > 0)
                ? item.work_action_areas[0]
                : null;
        }
        if (typeof actionSubarea === 'undefined' || actionSubarea === null) {
            actionSubarea = (item?.work_action_subareas &&
                Array.isArray(item.work_action_subareas) &&
                item.work_action_subareas.length > 0)
                ? item.work_action_subareas[0]
                : null;
        }
        if (typeof service === 'undefined' || service === null) {
            service = (item?.catalog_servicies &&
                Array.isArray(item.catalog_servicies) &&
                item.catalog_servicies.length > 0)
                ? item.catalog_servicies[0]
                : null;
        }
        if (typeof employee === 'undefined' || employee === null) {
            employee = item?.staff || null;
        }

        return (
            <>
                {employee && <Typography variant="body2" sx={styles.cardMuteText}>{t('Employee')}: {employee?.full_name}</Typography>}
                {actionArea && <Typography variant="body2" sx={styles.cardMuteText}>{t('Action area')}: {actionArea?.name}</Typography>}
                {actionSubarea && <Typography variant="body2" sx={styles.cardMuteText}>{t('Action subarea')}: {actionSubarea?.name}</Typography>}
                {service && <Typography variant="body2" sx={styles.cardMuteText}>{t('Work services')}: {service?.name}</Typography>}
            </>
        );
    };

    return (
        <>
            <div style={styles.flexList}>
                {items.map((item, index) => (
                    <Card sx={{ width: '100%' }}>
                        <CardHeader
                            action={
                                <>
                                    <IconButton
                                        aria-label="settings"
                                        onClick={(e) => handleMenuOpen(e, index)}>
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl)}
                                        onClose={handleMenuClose}
                                    >
                                        {/* <MenuItem onClick={handleView}>{t('View')}</MenuItem> */}
                                        <MenuItem onClick={handleEdit}>{t('Edit')}</MenuItem>
                                        <MenuItem onClick={() => { handleMenuClose(); setOpenDelete(true); }}>{t('Delete')}</MenuItem>
                                    </Menu>
                                </>
                            }
                            title={Consumption.fullTitle(item)}
                            subheader={t('Quantity') + ': ' + Consumption.displayQuantity(item)}
                            sx={styles.cardHeader}
                        />
                        <CardContent sx={styles.cardContent}>
                            {renderCardContent(item)}
                        </CardContent>
                    </Card>
                ))}
            </div>
            <DialogConfirmation open={openDelete} onResult={handleDeleteConfirmation} title={t('Delete consumable?')} >
                {t('You are about to delete this item. This action is not reversible!')}
            </DialogConfirmation>
        </>
    );
};

const CardListOfEmployeeHours = ({ items, planning, onAction }) => {

    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [openDelete, setOpenDelete] = useState(false);
    const { report, setReport } = useContext(ReportFormContext);

    const { token } = useToken();
    const abortController = new AbortController();
    const fetcherHoursCategories = () => fetchStaffHoursCategories(token, abortController.signal);
    const { data: hoursCategories, loading: loadingHoursCategories, error: errorHoursCategories } = useCachedData(
        "hours-categories", // Cache key
        fetcherHoursCategories,
        { useCache: true, forceRefresh: false }
    );

    const fromIndex = () => {
        return items[selectedIndex] || null;
    };

    const handleMenuOpen = (event, index) => {
        setAnchorEl(event.currentTarget);
        setSelectedIndex(index);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleView = () => {
        // Handle view action
        let item = fromIndex();
        console.log('View', item, selectedIndex);
        handleMenuClose();
        setSelectedIndex(null);

        if (item) {
            if (typeof onAction === 'function') {
                onAction('view', item, selectedIndex);
            } else {
                console.warn('onAction is not a function!');
            }
        } else {
            console.error('Report object is null!', item);
        }
    };

    const handleEdit = () => {
        // Handle edit action
        let item = fromIndex();
        // console.log('Edit', item, selectedIndex);
        handleMenuClose();
        setSelectedIndex(null);

        if (item) {
            if (typeof onAction === 'function') {
                onAction('edit', item, selectedIndex);
            } else {
                console.warn('onAction is not a function!');
            }
        } else {
            console.error('Report object is null!', item);
        }
    };

    const handleDeleteConfirmation = (result) => {
        // Handle delete action
        if (result) {
            let item = fromIndex();
            if (report && item) {
                if (selectedIndex >= 0 && selectedIndex < report.employees.length) {
                    report.employees.splice(selectedIndex, 1);
                }
                setReport({ ...report });
            } else {
                console.error('On delete employee hours: Report object missing or invalid index!', report, selectedIndex);
            }
        }
        setOpenDelete(false);
        handleMenuClose();
        setSelectedIndex(null);
    };

    const renderCardContent = (item) => {
        let actionArea = findActionAreaById(planning, item.workActionAreaId);
        let actionSubarea = findActionSubareaById(planning, item.workActionSubareaId);
        let service = findServiceById(planning, item.workServiceId);

        // Fallbacks. 
        // Try to get them from the item object. This will exists if the report data
        // is an edit and comes from an API call.
        if (typeof actionArea === 'undefined' || actionArea === null) {
            actionArea = (item?.work_action_areas &&
                Array.isArray(item.work_action_areas) &&
                item.work_action_areas.length > 0)
                ? item.work_action_areas[0]
                : null;
        }
        if (typeof actionSubarea === 'undefined' || actionSubarea === null) {
            actionSubarea = (item?.work_action_subareas &&
                Array.isArray(item.work_action_subareas) &&
                item.work_action_subareas.length > 0)
                ? item.work_action_subareas[0]
                : null;
        }
        if (typeof service === 'undefined' || service === null) {
            service = (item?.catalog_servicies &&
                Array.isArray(item.catalog_servicies) &&
                item.catalog_servicies.length > 0)
                ? item.catalog_servicies[0]
                : null;
        }

        return (
            <>
                {actionArea && <Typography variant="body2" sx={styles.cardMuteText}>{t('Action area')}: {actionArea?.name}</Typography>}
                {actionSubarea && <Typography variant="body2" sx={styles.cardMuteText}>{t('Action subarea')}: {actionSubarea?.name}</Typography>}
                {service && <Typography variant="body2" sx={styles.cardMuteText}>{t('Work services')}: {service?.name}</Typography>}
                {item.exclude_from_billing && <Typography variant="body2" sx={styles.cardInfoText}>{t('Heavy machinery/Truck')}</Typography>}
            </>
        );
    };

    const hoursCategoryName = (item) => {
        if (item.hoursCategoryId > 0 && hoursCategories) {
            // Get the hours category name from the hours_categories table
            const hoursCategory = hoursCategories.find(hc => hc.id === item.hoursCategoryId);
            if (hoursCategory) {
                return hoursCategory.name;
            }
        }

        return item.staff_hour_category?.name || '';
    };

    return (
        <>
            <div style={styles.flexList}>
                {items.map((item, index) => (
                    < Card sx={{ width: '100%' }}>
                        <CardHeader
                            action={
                                <>
                                    <IconButton
                                        aria-label="settings"
                                        onClick={(e) => handleMenuOpen(e, index)}>
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl)}
                                        onClose={handleMenuClose}
                                    >
                                        {/* <MenuItem onClick={handleView}>{t('View')}</MenuItem> */}
                                        <MenuItem onClick={handleEdit}>{t('Edit')}</MenuItem>
                                        <MenuItem onClick={() => { handleMenuClose(); setOpenDelete(true); }}>{t('Delete')}</MenuItem>
                                    </Menu>
                                </>
                            }
                            title={Employee.fullTitle(item, planning || null, t('Assigned to work group'))}
                            subheader={
                                <>
                                    {t('Hours') + ': ' + Employee.displayHours(item) + ' - '}
                                    <span style={{ color: 'rgb(144, 25, 210)' }}>{hoursCategoryName(item)}</span>
                                </>
                            }
                            sx={styles.cardHeader}
                        />
                        <CardContent sx={styles.cardContent}>
                            {renderCardContent(item)}
                        </CardContent>
                    </Card>
                ))}
            </div >
            <DialogConfirmation open={openDelete} onResult={handleDeleteConfirmation} title={t('Delete hours?')} >
                {t('You are about to delete this item. This action is not reversible!')}
            </DialogConfirmation>
        </>
    );
};

const CardListOfExtras = ({ items, planning, onAction }) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [openDelete, setOpenDelete] = useState(false);
    const { report, setReport } = useContext(ReportFormContext);

    // Using useRef to store selectedIndex
    const selectedIndexRef = useRef(null);

    // Fetch species data
    const { species } = useSpecies();
    const [speciesMap, setSpeciesMap] = useState({});
    useEffect(() => {
        if (species?.length) {
            setSpeciesMap(species.reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {}));
        }
    }, [species]);

    // Fetch actions data
    const { actions } = useActions();
    const [actionsMap, setActionsMap] = useState({});
    useEffect(() => {
        if (actions?.length) {
            setActionsMap(actions.reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {}));
        }
    }, [actions]);

    // Fetch statuses data
    const { statuses } = useStatuses();
    const [statusesMap, setStatusesMap] = useState({});
    useEffect(() => {
        if (statuses?.length) {
            setStatusesMap(statuses.reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {}));
        }
    }, [statuses]);

    const fromIndex = () => items[selectedIndexRef.current] || null;

    const handleMenuOpen = (event, index) => {
        setAnchorEl(event.currentTarget);
        selectedIndexRef.current = index; // Store the selected index in the ref
        console.debug('INDEX SELECTED', index, selectedIndexRef.current);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        let item = fromIndex();
        handleMenuClose();
        if (item && typeof onAction === "function") {
            onAction("edit", item, selectedIndexRef.current);
        }
        selectedIndexRef.current = null;
    };

    const handleDeleteConfirmation = (result) => {
        if (result) {
            let item = fromIndex();
            if (report && item) {
                if (selectedIndexRef.current >= 0 && selectedIndexRef.current < report.extras.length) {
                    report.extras.splice(selectedIndexRef.current, 1);
                }
                setReport({ ...report });
            }
        }
        setOpenDelete(false);
        handleMenuClose();
        selectedIndexRef.current = null;
    };

    const renderCardContent = (item) => {
        let actionArea = findActionAreaById(planning, item.workActionAreaId);
        let actionSubarea = findActionSubareaById(planning, item.workActionSubareaId);

        // Fallbacks. 
        // Try to get them from the item object. This will exists if the report data
        // is an edit and comes from an API call.
        if (typeof actionArea === 'undefined' || actionArea === null) {
            actionArea = (item?.work_action_areas &&
                Array.isArray(item.work_action_areas) &&
                item.work_action_areas.length > 0)
                ? item.work_action_areas[0]
                : null;
        }
        if (typeof actionSubarea === 'undefined' || actionSubarea === null) {
            actionSubarea = (item?.work_action_subareas &&
                Array.isArray(item.work_action_subareas) &&
                item.work_action_subareas.length > 0)
                ? item.work_action_subareas[0]
                : null;
        }

        return (
            <>
                {actionArea && <Typography variant="body2" sx={styles.cardMuteText}>{t('Action area')}: {actionArea?.name}</Typography>}
                {actionSubarea && <Typography variant="body2" sx={styles.cardMuteText}>{t('Action subarea')}: {actionSubarea?.name}</Typography>}
                {item?.feet && <Typography variant="body2">{t("Feet")}: {item.feet}</Typography>}
                {item?.notes && <Typography variant="body2">{t("Notes")}: {item.notes}</Typography>}
            </>
        );
    };

    return (
        <>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {items.map((item, index) => {
                    const specimen = speciesMap[item?.species_id] || item.species?.name || "";
                    const action = actionsMap[item?.action_id] || item.action?.name || "";
                    const status = statusesMap[item?.status_id] || item.status?.name || "";

                    return (
                        <Card key={index} sx={{ width: "100%" }}>
                            <CardHeader
                                action={
                                    <>
                                        <IconButton
                                            aria-label="settings"
                                            onClick={(e) => handleMenuOpen(e, index)}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl) && selectedIndexRef.current === index} // Use ref here
                                            onClose={handleMenuClose}
                                            anchorOrigin={{
                                                vertical: "bottom",
                                                horizontal: "right",
                                            }}
                                            transformOrigin={{
                                                vertical: "top",
                                                horizontal: "right",
                                            }}
                                        >
                                            <MenuItem onClick={handleEdit}>{t("Edit")}</MenuItem>
                                            <MenuItem onClick={() => { handleMenuClose(); setOpenDelete(true); }}>
                                                {t("Delete")}
                                            </MenuItem>
                                        </Menu>
                                    </>
                                }
                                title={`${specimen} - ${action}`}
                                subheader={status}
                                sx={styles.cardHeader}
                            />
                            <CardContent sx={styles.cardContent}>
                                {renderCardContent(item)}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
            <DialogConfirmation
                open={openDelete}
                onResult={handleDeleteConfirmation}
                title={t("Delete extra?")}
            >
                {t("You are about to delete this item. This action is not reversible!")}
            </DialogConfirmation>
        </>
    );
};

/**
 * Tab content 1 - Consumptions
 */
export const TabContent1 = ({ state }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [oldData, setOldData] = useState(null);
    const { report, setReport } = useContext(ReportFormContext);

    // console.debug('Current state:', state);

    const handleOpen = e => {
        e.preventDefault();
        setOpen(true);
    };

    const handleClose = e => {
        e.preventDefault();
        setOpen(false);
        setOldData(null);
    };

    const handleSubmit = () => {
        setOpen(false);
        setOldData(null);
    };

    const handleCardAction = (action, data, index) => {
        switch (action) {
            case 'edit':
                console.log('Edit', data, index);
                setOldData(data);
                setOpen(true);
                break;
            case 'delete':
                // Do nothing, we handle this inside the card component
                console.log('Delete');
                break;
            case 'view':
                console.log('View');
                break;
            default:
                console.error('Unknown action', action);
        }
    };

    const ListRecords = () => {
        // console.debug("List records comsumptions from report:", report);
        const items = report.consumptions;
        const count = items?.length || 0;

        if (count < 1) {
            // No content
            return (
                <>
                    <h3 style={{ textAlign: 'center' }}>{t('No items found')}</h3>
                </>
            );
        }

        return (
            <CardListOfConsumables onAction={handleCardAction} items={items} planning={state?.planning} />
        );
    };

    return (
        <>
            <Box sx={styles.topFabBox}>
                <Fab sx={{ paddingRight: '1rem' }} onClick={handleOpen} variant="extended" size="small" color="primary">
                    <PlusIcon sx={{ mr: 1 }} />
                    {t('Add consumption')}
                </Fab>
            </Box>
            <Box sx={{ overflowY: 'auto', height: '100%', p: 2 }}>
                <ListRecords />
            </Box>
            <FormDialog open={open} handleClose={handleClose} title={(oldData ? t('Edit consumption') : t('Add consumption'))}>
                <AddConsumptionForm oldData={oldData} onSubmit={handleSubmit} planning={state?.planning} />
            </FormDialog>
        </>
    );
};

/**
 * Tab content 2 - Employees
 */
export const TabContent2 = ({ state, edit = false }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [oldData, setOldData] = useState(null);
    const { report, setReport } = useContext(ReportFormContext);

    const handleOpen = e => {
        e.preventDefault();
        setOpen(true);
    };

    const handleClose = e => {
        e.preventDefault();
        setOpen(false);
        setOldData(null);
    };

    const handleSubmit = () => {
        setOpen(false);
        setOldData(null);
    };

    const handleCardAction = (action, data, index) => {
        switch (action) {
            case 'edit':
                console.log('Edit', data, index);
                setOldData(data);
                setOpen(true);
                break;
            case 'delete':
                // Do nothing, we handle this inside the card component
                console.log('Delete');
                break;
            case 'view':
                console.log('View');
                break;
            default:
                console.error('Unknown action', action);
        }
    };

    const ListRecords = () => {
        // NOTE: If reports comes from an edit (api request), 
        // the array field employees becomes hours.
        const items = report.hours || report.employees;
        const count = items?.length || 0;

        if (count < 1) {
            // No content
            return (
                <>
                    <h3 style={{ textAlign: 'center' }}>{t('No items found')}</h3>
                </>
            );
        }

        return (
            <CardListOfEmployeeHours onAction={handleCardAction} items={items} planning={state?.planning} />
        );
    };

    return (
        <>
            <Box sx={styles.topFabBox}>
                <Fab sx={{ paddingRight: '1rem' }} onClick={handleOpen} variant="extended" size="small" color="primary">
                    <PlusIcon sx={{ mr: 1 }} />
                    {t('Add hours')}
                </Fab>
            </Box>
            <Box sx={{ overflowY: 'auto', height: '100%', p: 2 }}>
                <ListRecords />
            </Box>
            <FormDialog open={open} handleClose={handleClose} title={(oldData ? t('Edit hours') : t('Add hours'))}>
                <AddEmployeeHoursForm oldData={oldData} onSubmit={handleSubmit} planning={state?.planning} />
            </FormDialog>
        </>
    )
};

/**
 * Tab content 3 - Photos
 */
export const TabContent3 = () => {
    const { t } = useTranslation();
    const { report, setReport } = useContext(ReportFormContext);

    const onChange = (updateFn) => {
        setReport((prevReport) => {
            const updatedPhotos = updateFn(prevReport.photos || []);
            return {
                ...prevReport,
                photos: updatedPhotos,
            };
        });
    };

    return (
        <>
            <Box sx={{ overflowY: 'auto', height: '100%', p: 2 }}>
                <PhotoManager photos={report.photos} setPhotos={onChange} requireNotes={true} />
            </Box>
        </>
    );
};

/**
 * Tab content 4 - Vehicles
 */
export const TabContent4 = ({ state, edit = false, vehicleCheckDefault = false }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const { report, setReport } = useContext(ReportFormContext);
    const hasVehicles = state?.planning?.vehicles && state.planning.vehicles.length > 0;
    const planningVehicles = state?.planning?.vehicles || [];

    useEffect(() => {
        // Edit an existing report
        if (edit && !report.vehiclesInitialized) {

            report.vehicles.forEach((vehicle) => {
                vehicle.checked = vehicle.was_used;
                vehicle.not_used_note = vehicle.notes || '';
            });

            setReport((prevReport) => {
                return {
                    ...prevReport,
                    vehicles: report.vehicles,
                    vehiclesInitialized: true,
                };
            });

            console.debug('TabContent4 in edit mode');
            return;
        }

        // Creation of a new report
        if (!edit) {
            // Vehicles
            if (hasVehicles && report.vehicles.length < 1) {
                // Is the first time, since report vehicles is empty
                // Initialize the vehicles report with the planning vehicles collection
                setReport((prevReport) => {
                    return {
                        ...prevReport,
                        vehicles: state.planning.vehicles.map((vehicle) => {
                            return { ...vehicle, checked: vehicleCheckDefault, not_used_note: '' };
                        }),
                    };
                })
            }
        }
    }, [edit]);

    /**
     * Helper function to get the ID of the object.
     * IMPORTANT: This is needed since depending of form data (New or Edit)
     * The vehicle and trailer records are different, one comes with just id,
     * the other comes with vehicle_id and trailer_id.
     * 
     * @param {Object} obj The object to check for ID value
     * @param {String} mainKey The main key to check, for example: vehicle_id
     * @param {String} altKey The alternative key to check
     * @returns 
     */
    const getID = (obj, mainKey = 'id', altKey = 'id') => {
        return obj[mainKey] || obj[altKey];
    };

    const findVehicleInPlanning = (vehicleId) => {
        return planningVehicles.find(v => getID(v, 'vehicle_id') === vehicleId);
    };

    const isVehicleChecked = (vehicleId) => {
        let vehicleMatch = report.vehicles.find(v => getID(v, 'vehicle_id') === vehicleId);
        return vehicleMatch?.checked || false;
    };

    const handleVehicleToggle = (vehicleId) => {
        setReport((prevReport) => {
            const updatedVehicles = prevReport.vehicles.map((vehicle) => {
                if (getID(vehicle, 'vehicle_id') === vehicleId) {
                    return { ...vehicle, checked: !vehicle.checked };
                }
                return vehicle;
            });

            return {
                ...prevReport,
                vehicles: updatedVehicles,
            };
        });
    };

    const handleRemoveVehicle = (vehicleId) => {
        setReport((prevReport) => {
            const updatedVehicles = prevReport.vehicles.filter((vehicle) => getID(vehicle, 'vehicle_id') !== vehicleId);
            return {
                ...prevReport,
                vehicles: updatedVehicles,
            };
        });
    };

    const handleOpen = e => {
        e.preventDefault();
        setOpen(true);
    };

    const handleClose = e => {
        e.preventDefault();
        setOpen(false);
    };

    const handleSubmit = (data) => {
        setOpen(false);
        // On submit, add the vehicle to the report list.
        // Append to the vehicle the property: not_from_planning = true.
        // and set the is_used property to true.
        setReport((prevReport) => {
            return {
                ...prevReport,
                vehicles: [...prevReport.vehicles, { ...data, not_from_planning: true, checked: true }],
            };
        });
    };

    const NotUsedVehicleField = ({ vehicle, disabled = false }) => {

        const { report, setReport } = useContext(ReportFormContext);
        const [localValue, setLocalValue] = useState('');

        // Initialize local state with the value from the context
        useEffect(() => {
            const matchVehicle = report.vehicles.find((v) => v.id === vehicle.id);
            const currentNote = matchVehicle?.not_used_note || '';

            setLocalValue(currentNote);
        }, [report.vehicles, vehicle.id]);

        const handleTextChange = (e) => {
            setLocalValue(e.target.value);
        };

        const handleBlur = () => {
            setReport((prevReport) => {
                const updatedVehicles = prevReport.vehicles.map((v) => {
                    if (v.id === vehicle.id) {
                        return { ...v, not_used_note: localValue };
                    }
                    return v;
                });

                return {
                    ...prevReport,
                    vehicles: updatedVehicles,
                };
            });
        };

        return (
            <TextField
                label={t('Not used note')}
                variant="outlined"
                value={localValue}
                onChange={handleTextChange}
                onBlur={handleBlur}
                disabled={disabled}
            />
        );
    };

    const VehicleItem = ({ vehicle }) => {

        let vehicleId = getID(vehicle, 'vehicle_id');

        // Assume that if the vehicle is not found in the planning, has been deleted
        // except if the vehicle is not_from_planning.
        // In that case, mark the item has disabled.
        let vehiclePlanning = findVehicleInPlanning(vehicleId);
        const isDisabled = !vehiclePlanning && !vehicle.not_from_planning;

        if (vehicle.not_from_planning) {
            vehiclePlanning = vehicle;
        }

        return (
            <Card key={vehicleId} variant="outlined" sx={{ opacity: isDisabled ? 0.6 : 1 }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <ListItemText
                            id={'vid-' + vehicleId}
                            primary={vehiclePlanning?.code || vehiclePlanning?.vehicle?.code || t('Not available')}
                            secondary={vehiclePlanning?.denomination || vehiclePlanning?.vehicle?.denomination || t('Not found')}
                            sx={{ m: 0 }}
                        />
                        {!vehicle.not_from_planning && (
                            <Switch
                                edge="end"
                                onChange={() => { handleVehicleToggle(vehicleId) }}
                                checked={isVehicleChecked(vehicleId)}
                                disabled={isDisabled}
                                inputProps={{
                                    'aria-labelledby': 'vid-' + vehicleId,
                                }}
                            />
                        )}
                        {vehicle.not_from_planning && (
                            <Button onClick={() => handleRemoveVehicle(vehicleId)}>
                                {t('Remove')}
                            </Button>
                        )}
                    </Box>
                    {!isVehicleChecked(vehicleId) && (
                        <NotUsedVehicleField vehicle={vehicle} disabled={isDisabled} />
                    )}
                    <VehicleUsageFields key={vehicle.id} vehicle={vehicle} />
                </CardContent>
            </Card>
        );
    };

    const VehicleConfirmationList = ({ vehicles }) => {
        return (
            <>
                <ul>
                    <ListSubheader sx={{ bgcolor: 'transparent', px: 0 }}>{t('Vehicles, trailers and machinery')}</ListSubheader>
                </ul>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
                    {vehicles.map((vehicle) => (
                        <VehicleItem key={getID(vehicle, 'vehicle_id')} vehicle={vehicle} />
                    ))}
                </Box>
            </>
        );
    };

    return (
        <>
            <Box sx={styles.topFabBox}>
                <Fab sx={{ paddingRight: '1rem' }}
                    onClick={handleOpen}
                    variant="extended"
                    size="small"
                    color="primary">
                    <PlusIcon sx={{ mr: 1 }} />
                    {t('Add vehicle')}
                </Fab>
            </Box>
            <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 300px)', display: 'block' }}>
                {(hasVehicles || report.vehicles.length > 0)
                    ? <VehicleConfirmationList vehicles={report.vehicles} />
                    : <Typography variant="h6">{t("No vehicles available")}</Typography>}
            </Box>
            <FormDialog open={open} handleClose={handleClose} title={t('Add vehicle')}>
                <AddVehicleForm
                    onSubmit={handleSubmit}
                    planning={state?.planning} />
            </FormDialog>
        </>
    );
};

/**
 * Tab content 5 - Extras
 */
export const TabContent5 = ({ state }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [oldData, setOldData] = useState(null);
    const { report, setReport } = useContext(ReportFormContext);

    const handleOpen = e => {
        e.preventDefault();
        setOpen(true);
    };

    const handleClose = e => {
        e.preventDefault();
        setOpen(false);
        setOldData(null);
    };

    const handleSubmit = () => {
        setOpen(false);
        setOldData(null);
    };

    const handleCardAction = (action, data, index) => {
        switch (action) {
            case 'edit':
                console.log('Edit', data, index);
                setOldData(data);
                setOpen(true);
                break;
            case 'delete':
                // Do nothing, we handle this inside the card component
                console.log('Delete');
                break;
            case 'view':
                console.log('View');
                break;
            default:
                console.error('Unknown action', action);
        }
    };

    const ListExtras = () => {
        const items = report.extras || [];
        const count = items?.length || 0;

        if (count < 1) {
            // No content
            return (
                <>
                    <h3 style={{ textAlign: 'center' }}>{t('No items found')}</h3>
                </>
            );
        }

        return (
            <CardListOfExtras onAction={handleCardAction} items={items} planning={state?.planning} />
        );
    };

    return (
        <>
            <Box sx={styles.topFabBox}>
                <Fab sx={{ paddingRight: '1rem' }} onClick={handleOpen} variant="extended" size="small" color="primary">
                    <PlusIcon sx={{ mr: 1 }} />
                    {t('Add extras')}
                </Fab>
            </Box>
            <Box sx={{ overflowY: 'auto', height: '100%', p: 2 }}>
                <ListExtras />
            </Box>
            <FormDialog open={open} handleClose={handleClose} title={(oldData ? t('Edit extras') : t('Add extras'))}>
                <AddExtrasForm oldData={oldData} onSubmit={handleSubmit} planning={state?.planning} />
            </FormDialog>
        </>
    );
}